/**
 * THE `blocks ⇄ editor JSON` CONVERTER - the last of the four things `adr/0008` makes this package
 * carry ("the block union types, the `blocks ⇄ editor JSON` converter, the D18 anchor maths, and the
 * client mirror of all eight of D21's rules"), and the one the cabinet's editor was waiting for
 * (register R-002, lens finding C2-02).
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ WHAT CROSSES, AND WHICH SIDE IS THE AUTHORITY
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * The editor is Tiptap (ProseMirror) and its document is a TREE: paragraphs, headings, and lists of
 * list items that nest. The platform's block is FLAT: one `body`, its paragraph rows (a style and a
 * list level each) and its mark rows, every offset a code point over `body`
 * (`authors-api.yaml`'s `TextBlock`; `anchors.ts`'s header). "The editor's own format never leaves
 * the browser" (`adr/0008-editor.md`): this file is the border, and both directions go through the
 * anchor maths next door rather than beside it - the paragraph rows ALWAYS come out of
 * `paragraphRowsFor` over the body this file built, and the marks ALWAYS out of `codec.ts`'s
 * `canonicaliseMarks` - so an editor save can never be refused `PARAGRAPH_ROWS_DISAGREE` or
 * `MARK_OUTSIDE_BLOCK_TEXT` (`anchors.ts`, the two refusals `article-editor.schema.ts` raises). That
 * is register R-001: the plain-text editor kept the server's old rows beside a new body.
 *
 * ★ THE NAMES ARE TIPTAP'S STARTER KIT'S, AND NOTHING ELSE IS IMPORTED FROM IT. This package has no
 * editor dependency and must not take one (the moderation worker imports it too), so the JSON is
 * typed structurally below and the node and mark names are the ones `@tiptap/starter-kit` registers.
 * `web/ui`'s `EditorShell` mounts that kit, and `web/ui/test/components/Editor/` checks every
 * document this file builds against the real schema.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★★ THE ONE SHAPE THE TWO MODELS DO NOT SHARE: A LIST THAT SKIPS A LEVEL
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * A flat row can say `ul` at level 2 straight after a level-0 item, or open a list at level 1; a
 * ProseMirror list item can only hold a list INSIDE an item one level up. So the tree built here
 * places such a paragraph at the deepest level that exists - the previous list paragraph's level
 * plus one, or 0 - which is what every renderer shows for it anyway. Nothing the editor itself
 * produces can skip a level, and `listLevel` never exceeds `MAX_LIST_LEVEL` (`ck_awpara_level`).
 * The round trip is therefore the identity on every block whose levels climb one step at a time,
 * and the property tests say exactly that (`test/shared/content-model/editor-json.spec.ts`).
 *
 * ★ TWO MORE SHAPES THE EDITOR CANNOT HOLD, both found by loading this file's documents into the
 * real schema (`web/ui/test/components/Editor/rich-text.spec.tsx`), not by reading:
 *   - A CODE SPAN CARRIES NO OTHER MARK. The kit's `code` mark excludes every other mark, so a text
 *     node that is both code and bold is "Invalid collection of marks" - and under the content check
 *     the whole block would open read-only. So a stored mark that runs over a code span is drawn
 *     AROUND it, and the next save stores it that way. The same exclusion is what stops the editor
 *     ever writing the arrangement `codec.ts` refuses - a mark whose edge falls inside a code span.
 *   - TWO LINKS OVER THE SAME CHARACTERS (a link excludes a link), which have no markdown either
 *     (`codec.ts`, `refuseInexpressibleMarks`). The one that starts first keeps the overlap.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * ★ WHAT THE EDITOR SIDE IS CLEANED OF ON THE WAY OUT, AND WHY EACH IS A REFUSAL AVOIDED
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 *   - A LINE BREAK INSIDE A PARAGRAPH (`\n`, `\r`) becomes a space. U+000A is the paragraph
 *     separator in `body`, so a stray one would be a paragraph the editor never drew, and
 *     `ck_awb_body_nocr` refuses a carriage return outright.
 *   - U+0000 is dropped: a Postgres `text` column cannot hold it at all.
 *   - THE BODY IS NFC. `ck_awb_body_nfc` is live and the save does not normalise, so a pasted
 *     decomposed "é" would reach the database as a CHECK violation - a 500. The mark offsets are
 *     measured AFTER the normalisation, over the string that is sent (see `normaliseLine`).
 *   - A LINK whose address is not `https://`, `http://` or `mailto:` is dropped as a mark and its
 *     text kept: `LINK_HREF_RE` is the database's own `ck_awmark_href` and the contract's pattern.
 *   - A mark Tiptap has and the contract does not (none in the kit this package names) is dropped.
 *
 * C11: no clock is read here and no instant is taken.
 */
import type { InlineMarkKind, ParagraphStyle } from './blocks';
import {
  DEFAULT_PARAGRAPH_STYLE,
  HEADING_PARAGRAPH_STYLES,
  INLINE_MARK_KINDS,
  LINK_HREF_RE,
  MAX_LIST_LEVEL,
  headingDepth,
  isListStyle,
} from './blocks';
import type { MarkRow, ParagraphRow, ParagraphStyling } from './anchors';
import { PARAGRAPH_SEPARATOR, codePointsOf, paragraphRowsFor } from './anchors';
import { canonicaliseMarks } from './codec';

/** One ProseMirror mark, as `editor.getJSON()` spells it. */
export interface EditorMark {
  readonly type: string;
  readonly attrs?: Readonly<Record<string, unknown>> | undefined;
}

/**
 * One ProseMirror node, as `editor.getJSON()` spells it. Structural, so Tiptap's own `JSONContent`
 * is accepted as it is - its `type` is optional there, so it is here.
 */
export interface EditorNode {
  readonly type?: string | undefined;
  readonly attrs?: Readonly<Record<string, unknown>> | undefined;
  readonly content?: readonly EditorNode[] | undefined;
  readonly text?: string | undefined;
  readonly marks?: readonly EditorMark[] | undefined;
}

/** The two kinds with editable, formatted text. An image's caption and a divider have no marks. */
export type EditableKind = 'text' | 'quote';

/** What the editor gives back: the three members a text block's save carries (a quote: no rows). */
export interface EditableText {
  readonly body: string;
  /** The rows `paragraphRowsFor` derives. Always `[]` for a quote, which declares no `paragraphs`. */
  readonly paragraphs: readonly ParagraphRow[];
  readonly marks: readonly MarkRow[];
}

/** The node names `@tiptap/starter-kit` registers, for the nodes this model has. */
export const EDITOR_NODE = {
  doc: 'doc',
  paragraph: 'paragraph',
  heading: 'heading',
  bulletList: 'bulletList',
  orderedList: 'orderedList',
  listItem: 'listItem',
  text: 'text',
  hardBreak: 'hardBreak',
} as const;

/** `inline_mark_kind.code` → the starter kit's mark name. Six rows, six marks, one each way. */
export const EDITOR_MARK_OF: Readonly<Record<InlineMarkKind, string>> = {
  strong: 'bold',
  em: 'italic',
  underline: 'underline',
  strike: 'strike',
  code: 'code',
  link: 'link',
};

const MARK_KIND_OF: ReadonlyMap<string, InlineMarkKind> = new Map(
  INLINE_MARK_KINDS.map((kind) => [EDITOR_MARK_OF[kind], kind] as const),
);

/** The heading levels the model has - `h1`..`h4` (`paragraph_style`, `WC-CR-09` ruled (c)). */
export const EDITOR_HEADING_LEVELS: readonly number[] = HEADING_PARAGRAPH_STYLES.map(
  (_style, index) => index + 1,
);

// ------------------------------------------------------------------------------------------------
// Blocks → editor JSON
// ------------------------------------------------------------------------------------------------

/** One inline run with the marks that cover all of it. */
interface Run {
  readonly text: string;
  readonly marks: readonly MarkRow[];
}

function editorMarkOf(mark: MarkRow): EditorMark {
  return mark.kind === 'link'
    ? { type: EDITOR_MARK_OF.link, attrs: { href: mark.linkHref } }
    : { type: EDITOR_MARK_OF[mark.kind] };
}

function sameMarks(left: readonly MarkRow[], right: readonly MarkRow[]): boolean {
  return (
    left.length === right.length &&
    left.every(
      (mark, index) => mark.kind === right[index]?.kind && mark.linkHref === right[index]?.linkHref,
    )
  );
}

/**
 * One paragraph's text cut into runs at every mark edge, each run carrying the marks over it.
 *
 * `marks` are this paragraph's, already rebased to it and canonical (so none crosses its edge). A
 * run inside a code span carries the code mark alone, and a run under two links keeps the one that
 * starts first - both in the header. Adjacent runs that end up
 * with the same marks are joined, which is what ProseMirror does to adjacent text nodes itself, so
 * the JSON here is the JSON the editor will hand back.
 */
function runsOf(text: readonly string[], marks: readonly MarkRow[]): readonly Run[] {
  const edges = new Set<number>([0, text.length]);
  for (const mark of marks) {
    edges.add(mark.startCp);
    edges.add(mark.endCp);
  }
  const points = [...edges].sort((left, right) => left - right);
  const runs: Run[] = [];
  for (let index = 0; index + 1 < points.length; index += 1) {
    const from = points[index] ?? 0;
    const to = points[index + 1] ?? 0;
    if (to <= from) {
      continue;
    }
    const covering = marks.filter((mark) => mark.startCp <= from && mark.endCp >= to);
    const code = covering.find((mark) => mark.kind === 'code');
    const link = covering
      .filter((mark) => mark.kind === 'link')
      .sort((left, right) => left.startCp - right.startCp)[0];
    const kept =
      code !== undefined
        ? [code]
        : INLINE_MARK_KINDS.flatMap((kind) => {
            if (kind === 'link') {
              return link === undefined ? [] : [link];
            }
            const found = covering.find((mark) => mark.kind === kind);
            return found === undefined ? [] : [found];
          });
    const piece = text.slice(from, to).join('');
    const last = runs[runs.length - 1];
    if (last !== undefined && sameMarks(last.marks, kept)) {
      runs[runs.length - 1] = { text: last.text + piece, marks: last.marks };
      continue;
    }
    runs.push({ text: piece, marks: kept });
  }
  return runs;
}

function inlineOf(runs: readonly Run[]): readonly EditorNode[] {
  return runs.map((run) =>
    run.marks.length === 0
      ? { type: EDITOR_NODE.text, text: run.text }
      : { type: EDITOR_NODE.text, text: run.text, marks: run.marks.map(editorMarkOf) },
  );
}

/** A paragraph or heading node. An empty one has no `content`: ProseMirror refuses an empty text node. */
function textblockOf(style: ParagraphStyle, runs: readonly Run[]): EditorNode {
  const content = runs.length === 0 ? {} : { content: inlineOf(runs) };
  const depth = headingDepth(style);
  return depth === null
    ? { type: EDITOR_NODE.paragraph, ...content }
    : { type: EDITOR_NODE.heading, attrs: { level: depth }, ...content };
}

/** A list being built: mutable only inside `listTree`, frozen into `EditorNode`s at the end. */
interface OpenList {
  readonly type: string;
  readonly items: OpenItem[];
}
interface OpenItem {
  readonly children: (EditorNode | OpenList)[];
}

function isOpenList(child: EditorNode | OpenList): child is OpenList {
  return 'items' in child;
}

function closeList(list: OpenList): EditorNode {
  return {
    type: list.type,
    content: list.items.map((item) => ({
      type: EDITOR_NODE.listItem,
      content: item.children.map((child) => (isOpenList(child) ? closeList(child) : child)),
    })),
  };
}

/**
 * Flat rows → the document's top-level nodes, lists nested.
 *
 * `open[d]` is the list at depth `d` that the next item of that depth joins. A list paragraph goes
 * to depth `min(listLevel, open.length)` - at most one below the deepest open list, which is the
 * clamp the header describes - and a list of the other kind at that depth starts a new sibling list
 * rather than joining (`ul` then `ol` at level 0 are two lists, as they are in markdown).
 */
function topLevelNodes(
  rows: readonly ParagraphRow[],
  runs: readonly (readonly Run[])[],
): readonly EditorNode[] {
  const nodes: (EditorNode | OpenList)[] = [];
  let open: { list: OpenList; item: OpenItem | null }[] = [];

  for (const [index, row] of rows.entries()) {
    const paragraphRuns = runs[index] ?? [];
    if (!isListStyle(row.style)) {
      open = [];
      nodes.push(textblockOf(row.style, paragraphRuns));
      continue;
    }
    const type = row.style === 'ol' ? EDITOR_NODE.orderedList : EDITOR_NODE.bulletList;
    const depth = Math.min(row.listLevel, open.length, MAX_LIST_LEVEL);
    open = open.slice(0, depth + 1);
    let level = open[depth];
    if (level === undefined || level.list.type !== type) {
      const list: OpenList = { type, items: [] };
      const parent = depth === 0 ? null : open[depth - 1]?.item;
      if (parent === null || parent === undefined) {
        nodes.push(list);
      } else {
        parent.children.push(list);
      }
      level = { list, item: null };
      open = [...open.slice(0, depth), level];
    }
    const item: OpenItem = { children: [textblockOf(DEFAULT_PARAGRAPH_STYLE, paragraphRuns)] };
    level.list.items.push(item);
    level.item = item;
  }
  return nodes.map((node) => (isOpenList(node) ? closeList(node) : node));
}

/**
 * ★ A TEXT OR QUOTE BLOCK AS THE EDITOR'S DOCUMENT.
 *
 * `paragraphs` are the block's rows as the server sent them; only their STYLES are read, by
 * position - the ranges are recomputed from `body` by `paragraphRowsFor`, which is the server's own
 * rule (`anchors.ts`). A quote has no rows: every line of it is a plain paragraph. `marks` go
 * through `canonicaliseMarks` first - clamped to the text, split at paragraph breaks, touching runs
 * of one kind merged - so each lands inside exactly one paragraph.
 */
export function editorDocFromText(
  kind: EditableKind,
  body: string,
  paragraphs: readonly ParagraphStyling[] = [],
  marks: readonly MarkRow[] = [],
): EditorNode {
  const rows = paragraphRowsFor(body, kind === 'text' ? paragraphs : []);
  const allowed = marks.filter(
    (mark) => mark.kind !== 'link' || (mark.linkHref !== null && LINK_HREF_RE.test(mark.linkHref)),
  );
  const canonical = canonicaliseMarks(body, allowed);
  const cps = codePointsOf(body);
  const runs = rows.map((row) =>
    runsOf(
      cps.slice(row.startCp, row.endCp),
      canonical
        .filter((mark) => mark.startCp >= row.startCp && mark.endCp <= row.endCp)
        .map((mark) => ({
          ...mark,
          startCp: mark.startCp - row.startCp,
          endCp: mark.endCp - row.startCp,
        })),
    ),
  );
  const content =
    kind === 'text'
      ? topLevelNodes(rows, runs)
      : runs.map((paragraphRuns) => textblockOf(DEFAULT_PARAGRAPH_STYLE, paragraphRuns));
  return { type: EDITOR_NODE.doc, content };
}

// ------------------------------------------------------------------------------------------------
// Editor JSON → blocks
// ------------------------------------------------------------------------------------------------

/** One inline run as the editor had it: its raw text and the contract's marks over it. */
interface RawRun {
  readonly text: string;
  readonly kinds: readonly { readonly kind: InlineMarkKind; readonly linkHref: string | null }[];
}

/** One paragraph as the editor had it. */
interface Line {
  readonly style: ParagraphStyle;
  readonly listLevel: number;
  readonly runs: readonly RawRun[];
}

/** U+0000, built rather than typed: `no-control-regex`, and an invisible character in source is deleted. */
const NUL = String.fromCodePoint(0);

/** `\r\n`, `\r` and `\n` become one space each; U+0000 goes. See the header. */
function cleanText(text: string): string {
  return text.replace(/\r\n|\r|\n/gu, ' ').replaceAll(NUL, '');
}

function contractMarksOf(
  marks: readonly EditorMark[] | undefined,
): { kind: InlineMarkKind; linkHref: string | null }[] {
  const out: { kind: InlineMarkKind; linkHref: string | null }[] = [];
  for (const mark of marks ?? []) {
    const kind = MARK_KIND_OF.get(mark.type);
    if (kind === undefined || out.some((kept) => kept.kind === kind)) {
      continue;
    }
    if (kind !== 'link') {
      out.push({ kind, linkHref: null });
      continue;
    }
    const href = mark.attrs?.['href'];
    if (typeof href === 'string' && LINK_HREF_RE.test(href)) {
      out.push({ kind, linkHref: href });
    }
  }
  // Code excludes every other mark in the editor (see the header), and in both directions here.
  const code = out.find((kept) => kept.kind === 'code');
  return code === undefined ? out : [code];
}

function isTextblock(node: EditorNode): boolean {
  if (node.type === EDITOR_NODE.paragraph || node.type === EDITOR_NODE.heading) {
    return true;
  }
  const content = node.content ?? [];
  return (
    content.length > 0 &&
    content.every(
      (child) => child.type === EDITOR_NODE.text || child.type === EDITOR_NODE.hardBreak,
    )
  );
}

function runsOfTextblock(node: EditorNode): readonly RawRun[] {
  const runs: RawRun[] = [];
  for (const child of node.content ?? []) {
    if (child.type === EDITOR_NODE.text) {
      const text = cleanText(child.text ?? '');
      if (text.length > 0) {
        runs.push({ text, kinds: contractMarksOf(child.marks) });
      }
    } else if (child.type === EDITOR_NODE.hardBreak) {
      // THE KIT'S HARD BREAK IS OFF in `EditorShell`; a document that carries one anyway keeps the
      // two words apart rather than welding them together.
      runs.push({ text: ' ', kinds: [] });
    }
  }
  return runs;
}

function styleOf(node: EditorNode): ParagraphStyle {
  if (node.type !== EDITOR_NODE.heading) {
    return DEFAULT_PARAGRAPH_STYLE;
  }
  const level = node.attrs?.['level'];
  const index = typeof level === 'number' && Number.isInteger(level) ? level - 1 : 0;
  return (
    HEADING_PARAGRAPH_STYLES[Math.max(0, Math.min(index, HEADING_PARAGRAPH_STYLES.length - 1))] ??
    DEFAULT_PARAGRAPH_STYLE
  );
}

/** Where a node sits: at the top, or inside an item of a list of this style at this depth. */
interface ListContext {
  readonly style: 'ul' | 'ol';
  readonly depth: number;
}

/**
 * The tree → flat lines, in document order.
 *
 * EVERY TEXTBLOCK INSIDE A LIST ITEM IS A LIST PARAGRAPH of that item's list, at that depth: the
 * flat model has no "second paragraph of one bullet", so a list item holding two becomes two items
 * on the next open. A node this model has no name for is walked through for the text inside it -
 * nothing an author typed is dropped because the editor wrapped it in something unexpected.
 */
function linesOf(nodes: readonly EditorNode[], context: ListContext | null, into: Line[]): void {
  for (const node of nodes) {
    if (node.type === EDITOR_NODE.bulletList || node.type === EDITOR_NODE.orderedList) {
      const style = node.type === EDITOR_NODE.orderedList ? 'ol' : 'ul';
      const depth = context === null ? 0 : context.depth + 1;
      for (const item of node.content ?? []) {
        linesOf(
          item.type === EDITOR_NODE.listItem ? (item.content ?? []) : [item],
          { style, depth },
          into,
        );
      }
      continue;
    }
    if (isTextblock(node)) {
      into.push(
        context === null
          ? { style: styleOf(node), listLevel: 0, runs: runsOfTextblock(node) }
          : {
              style: context.style,
              listLevel: Math.min(context.depth, MAX_LIST_LEVEL),
              runs: runsOfTextblock(node),
            },
      );
      continue;
    }
    linesOf(node.content ?? [], context, into);
  }
}

/**
 * ★ ONE LINE'S TEXT IN NFC, AND WHERE EACH RUN ENDS IN IT, IN CODE POINTS.
 *
 * The common case is text that is already NFC, and the ends are simply the runs' lengths. When it is
 * not - a decomposed "é" pasted in - the line is normalised as a whole (NFC composes across a mark
 * edge, so run by run would not do) and each run's end is the length of the normalised PREFIX that
 * ends there, kept in order and inside the line. A run that composes into its neighbour ends up
 * zero-width and its marks go with it, which is the honest outcome for formatting on half a letter.
 */
function normaliseLine(runs: readonly RawRun[]): { text: string; ends: readonly number[] } {
  const raw = runs.map((run) => run.text).join('');
  const text = raw.normalize('NFC');
  const ends: number[] = [];
  if (text === raw) {
    let end = 0;
    for (const run of runs) {
      end += codePointsOf(run.text).length;
      ends.push(end);
    }
    return { text, ends };
  }
  const total = codePointsOf(text).length;
  let prefix = '';
  let previous = 0;
  for (const run of runs) {
    prefix += run.text;
    const end = Math.max(previous, Math.min(total, codePointsOf(prefix.normalize('NFC')).length));
    ends.push(end);
    previous = end;
  }
  return { text, ends };
}

/**
 * ★ THE EDITOR'S DOCUMENT AS A BLOCK'S `body`, `paragraphs` AND `marks` - ready to send.
 *
 * The paragraph rows are `paragraphRowsFor(body, styles)` and the marks `canonicaliseMarks(body, …)`,
 * so every member here is what the server derives from the same text: the save's refusals
 * (`refuseParagraphRows`, `refuseMarkRows`, `inlineMarkSchema`'s href and width rules and
 * `paragraphSchema`'s level band) have nothing to find. A quote's lines are joined the same way,
 * its styles are not read, and it answers no rows. A document with no text at all is one empty
 * paragraph - `paragraphRangesFor('')` is one range, and an empty text block is a legal draft.
 */
export function textFromEditorDoc(kind: EditableKind, doc: EditorNode): EditableText {
  const lines: Line[] = [];
  linesOf(doc.content ?? [], null, lines);

  const texts: string[] = [];
  const styles: ParagraphStyling[] = [];
  const marks: MarkRow[] = [];
  let offset = 0;
  for (const line of lines) {
    const { text, ends } = normaliseLine(line.runs);
    let start = 0;
    for (const [index, run] of line.runs.entries()) {
      const end = ends[index] ?? start;
      if (end > start) {
        for (const mark of run.kinds) {
          marks.push({
            startCp: offset + start,
            endCp: offset + end,
            kind: mark.kind,
            linkHref: mark.linkHref,
          });
        }
      }
      start = end;
    }
    texts.push(text);
    styles.push(
      kind === 'text'
        ? { style: line.style, listLevel: isListStyle(line.style) ? line.listLevel : 0 }
        : { style: DEFAULT_PARAGRAPH_STYLE, listLevel: 0 },
    );
    offset += codePointsOf(text).length + PARAGRAPH_SEPARATOR.length;
  }

  const body = texts.join(PARAGRAPH_SEPARATOR);
  return {
    body,
    paragraphs: kind === 'text' ? paragraphRowsFor(body, styles) : [],
    marks: canonicaliseMarks(body, marks),
  };
}
